import useSWR from "swr";

import StaticOptions from "../assets/static/options.json";
import { useCallback, useMemo } from "react";
import { fetcher } from "src/services/config";
import { ServicesURL } from "@utils/utilsApi";
import { useTranslation } from "react-i18next";
import { KeyURL, MethodType } from "src/services/type";
import { useMeridiemMode } from "@mui/x-date-pickers/internals/hooks/date-helpers-hooks";
import { useParams } from "react-router-dom";

const OptionObj: any = {
  getFormats: {
    value: "id",
    label: "description",
  },
  getBadges: {
    value: "id",
    label: "name",
  },
  getCertificates: {
    value: "id",
    label: "name",
  },
  getLanguages: {
    value: "id",
    label: "label",
  },
  getRoles: {
    value: "roleKey",
    label: "roleKey",
    custom: "customRoleDescription",
  },
  getCampaignLearningObjects: {
    value: "id",
    label: "name",
  },
  findGroups: {
    value: "id",
    label: "name",
  },
  getBankAccountList: {
    value: "id",
    custom: "sap_number",
  },
  getBankAccountListAll: {
    value: "id",
    custom: "sap_number",
  },
  getDescriptions: {
    value: "id",
    label: "title",
  },
  getCostCenterList: {
    value: "id",
    composeLabel: ["sap_code", "function"],
  },
};

export const useFetchOptions = (
  service?: string,
  optionsKey?: string,
  options?: string[] | undefined,
  queryParams?: Record<string, any>,
) => {
  const { id = "1" } = useParams();
  const { t } = useTranslation("input");

  const customServiceURL = useMemo(() => {
    if (!service || !queryParams) return null;

    let url = ServicesURL[service as KeyURL];

    Object.keys(queryParams).forEach((key) => {
      url = url.replace(key, queryParams[key]);
    });

    return url;
  }, [service, queryParams]);

  const { data: dataOptions, isLoading } = useSWR(
    customServiceURL ||
      (service
        ? service === "corporateLanguages"
          ? ServicesURL.corporateLanguages.replace(":corporateId", id)
          : `${ServicesURL[service as KeyURL]}${service === "getRoles" ? `?corporate=${id}` : ""}`
        : null),
    (url: string) => {
      return fetcher(url, MethodType.GET);
    },
  );

  const getOptions = useCallback(() => {
    if (options && options.length > 0) {
      const objectFormatted = options.some(
        (item: any) => typeof item === "object",
      );

      if (!!objectFormatted) {
        return options;
      }

      return options.map((value) => {
        return {
          value,
          label: t(`options.${optionsKey}.${value.toString().toLowerCase()}`),
        };
      });
    } else if (optionsKey) {
      const options =
        StaticOptions.find((option) => option.key === optionsKey)?.options ||
        [];
      return options.map((value) => {
        return {
          value,
          label: t(`options.${optionsKey}.${value.toString().toLowerCase()}`),
        };
      });
    } else if (service === "corporateLanguages") {
      if (!dataOptions) return [];

      return [
        dataOptions.primary_language,
        ...(dataOptions.secondary_languages || []),
      ]
        .filter(Boolean)
        .map((lang: { id: string; label: string; code?: string }) => ({
          value: lang.id,
          label: String(
            t(
              `options.language.${String(lang.code ?? lang.id).toLowerCase()}`,
              {
                defaultValue: lang.label,
              },
            ),
          ),
        }));
    } else if (service) {
      const newDataOptions = dataOptions?.map((el: any) => {
        const option = OptionObj[service!];

        const value = el[option.value];
        const customLabel = el[option.custom];

        if (option.composeLabel) {
          const composedLabel = option.composeLabel
            .map((key: string) => el[key])
            .join(" - ");

          return {
            value,
            label: composedLabel.toLowerCase(),
          };
        }

        const fallbackLabel = el[option.label];

        return {
          value,
          label: customLabel ?? fallbackLabel.toLowerCase(),
        };
      });

      return newDataOptions;
    }

    return [];
  }, [dataOptions, optionsKey, service, t, options]);
  return { getOptions, isLoading };
};
